import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'data-fetching-client';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UIModal from 'UIComponents/dialog/UIModal';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import { CLOSE_OVERRIDE_MODAL_WITHOUT_SAVE, MAINTAIN_USER_DEFINED_COLUMNS, OVERWRITE_USER_DEFINED_COLUMNS } from '../../constants/ForecastType';
import { UPDATE_REP_DEAL_COLUMNS } from '../data/dataFetchingQueries';
import { showAllColumnsSavedAlert, showColumnsOverriddenAlert, showColumnsMaintainedAlert, showOverrideColumnsFailureAlert } from '../../utils/alertUtils';
import { shouldTrack } from '../../utils/trackerUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OverrideConfirmationModal = ({
  forecastTypeId,
  onClose,
  onTrack
}) => {
  var _overrideData$overrid;
  const [selection, setSelection] = useState(undefined);
  const [isOverrideDryRun, setIsOverrideDryRun] = useState(true);

  // Cant be cached since columns are not part of this API call
  // dataFetchingClient would never know when to recompute vs use cached vaue
  const {
    loading: overrideDataLoading,
    data: overrideData,
    error: overrideError
  } = useQuery(UPDATE_REP_DEAL_COLUMNS, {
    variables: {
      forecastTypeId,
      isDryRun: isOverrideDryRun
    },
    fetchPolicy: 'no-cache'
  });
  const numberOfUserOverrides = (_overrideData$overrid = overrideData === null || overrideData === void 0 ? void 0 : overrideData.overrideRepDealColumns) !== null && _overrideData$overrid !== void 0 ? _overrideData$overrid : 0;
  const handleSelectMaintainColumns = useCallback(() => {
    setSelection('maintain');
  }, []);
  const handleSelectOverrideColumns = useCallback(() => {
    setSelection('override');
  }, []);
  const handleOnSave = useCallback(() => {
    if (selection === 'maintain') {
      showColumnsMaintainedAlert(numberOfUserOverrides);
      if (shouldTrack(onTrack)) {
        onTrack === null || onTrack === void 0 || onTrack(MAINTAIN_USER_DEFINED_COLUMNS);
      }
      onClose();
      return;
    }
    if (selection === 'override') {
      if (shouldTrack(onTrack)) {
        onTrack === null || onTrack === void 0 || onTrack(OVERWRITE_USER_DEFINED_COLUMNS);
      }
      setIsOverrideDryRun(false);
    }
  }, [selection, numberOfUserOverrides, onTrack, onClose]);
  const handleOnCloseWithoutSave = useCallback(() => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(CLOSE_OVERRIDE_MODAL_WITHOUT_SAVE);
    }
    onClose();
    showColumnsMaintainedAlert(numberOfUserOverrides);
  }, [numberOfUserOverrides, onClose, onTrack]);

  // After querying number of overrides, if there is an error, no data, or data was written, close the modal and show a toast
  useEffect(() => {
    if (overrideError) {
      showOverrideColumnsFailureAlert();
      onClose();
      return;
    }

    // If the dry run computes 0 users, close the modal and show a toast
    if (!overrideDataLoading && numberOfUserOverrides <= 0) {
      showAllColumnsSavedAlert();
      onClose();
    }

    // Case: override successful - close the modal flow and show a toast
    if (!overrideDataLoading && !isOverrideDryRun) {
      showColumnsOverriddenAlert(numberOfUserOverrides);
      onClose();
    }
  }, [overrideError, numberOfUserOverrides, isOverrideDryRun, onClose, overrideDataLoading]);
  if (overrideDataLoading) {
    return /*#__PURE__*/_jsx(UILoadingOverlay, {
      contextual: true
    });
  }
  if (overrideError || numberOfUserOverrides <= 0 || !isOverrideDryRun) {
    return null;
  }
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "danger",
    width: 580,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'overrideConfirmationModal.title',
          options: {
            count: numberOfUserOverrides
          }
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: handleOnCloseWithoutSave
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "500",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "overrideConfirmationModal.description",
          options: {
            count: numberOfUserOverrides
          }
        }), /*#__PURE__*/_jsxs(UIToggleGroup, {
          name: "override-column-select",
          children: [/*#__PURE__*/_jsx(UIRadioInput, {
            onChange: handleSelectMaintainColumns,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "overrideConfirmationModal.maintainOptions",
              options: {
                count: numberOfUserOverrides
              }
            })
          }), /*#__PURE__*/_jsx(UIRadioInput, {
            onChange: handleSelectOverrideColumns,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "overrideConfirmationModal.overrideOption",
              options: {
                count: numberOfUserOverrides
              }
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: /*#__PURE__*/_jsx(UIButton, {
        disabled: !selection,
        onClick: handleOnSave,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "overrideConfirmationModal.saveButtonText"
        })
      })
    })]
  });
};
export default OverrideConfirmationModal;